<template>
	<TnParagraph
		size="l"
		responsive
		bold
		class="margin-bottom-m"
	>
		Hvor mye vil du betale nå?
	</TnParagraph>
	<div class="slider-wrapper">
		<TnButton
			:disabled="selectedAmount === 0"
			tertiary
			iconOnly="MinusCircleOutline"
			@click="adjustSelectedAmount(-amountIncrements)"
		>
			Trekk fra {{ formattedAmountIncrements }} i forskuddsbetaling.
		</TnButton>
		<label>
			<div>
				<datalist id="values">
					<option
						:value="0"
						label="0,-"
					></option>
					<option
						:value="5000"
						:label="formatPrice.oneTime(maxAmount)"
					></option>
				</datalist>
				<input
					id="amount-range"
					type="range"
					:min="0"
					:max="maxAmount"
					:step="amountIncrements"
					v-model.number="selectedAmount"
				/>
			</div>
		</label>

		<TnButton
			:disabled="selectedAmount === maxAmount"
			tertiary
			iconOnly="PlusCircleOutline"
			@click="adjustSelectedAmount(amountIncrements)"
		>
			Legg til {{ formattedAmountIncrements }} i forskuddsbetaling.
		</TnButton>
	</div>
	<p class="margin-bottom-l selected-amount">
		{{ formatPrice.oneTime(selectedAmount) }}
	</p>
</template>

<script setup lang="ts">
import { formatPrice } from "~/helpers/formatting/price";

const selectedAmount = defineModel<number>();
const { maxAmount, amountIncrements } = defineProps<{
	maxAmount: number;
	amountIncrements: number;
}>();

const formattedAmountIncrements = computed(() => formatPrice.oneTime(amountIncrements));

const adjustSelectedAmount = (adjustment: number) => {
	selectedAmount.value += adjustment;

	if (selectedAmount.value > maxAmount) {
		selectedAmount.value = maxAmount;
	}
};

const adjustSliderProgress = () => {
	const sliderEl = document.getElementById("amount-range") as HTMLInputElement;
	const progress = (selectedAmount.value / maxAmount) * 100;
	sliderEl.style.background = `linear-gradient(to right, #1020F9 ${progress}%, #D6E7FF ${progress}%)`;
};

watch([selectedAmount, () => maxAmount], () => {
	adjustSliderProgress();
});

onMounted(() => {
	adjustSliderProgress();
});
</script>

<style scoped lang="scss">
.selected-amount {
	@include font-text-bold-l;
	@include breakpoint(mobile) {
		@include font-text-bold-xl;
	}
}
.slider-wrapper {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	height: 66px;
	@include breakpoint(mobile) {
		height: 48px;
	}

	button {
		position: relative;
		top: -1px;
		flex: 0;
	}

	label {
		display: flex;
		flex: 1;

		div {
			display: flex;
			flex-direction: column;
			flex: 1;
			position: relative;
			top: -17px;
			@include breakpoint(mobile) {
				top: -13px;
			}

			datalist {
				display: inline-flex;
				justify-content: space-between;
				margin-bottom: 8px;
				@include font-text-s;
			}

			input[type="range"] {
				/* removing default appearance */
				-webkit-appearance: none;
				appearance: none;
				/* creating a custom design */
				width: 100%;
				cursor: pointer;
				outline: none;
				border-radius: 10px;
				/*  overflow: hidden;  remove this line*/

				/* New additions */
				height: 8px;
				background: #d6e7ff;
			}

			/* Thumb: webkit */
			input[type="range"]::-webkit-slider-thumb {
				/* removing default appearance */
				-webkit-appearance: none;
				appearance: none;
				/* creating a custom design */
				height: 20px;
				width: 20px;
				background-color: #070452;
				border-radius: 50%;
				border: none;
				transition: 0.2s ease-in-out;
			}

			/* Thumb: Firefox */
			input[type="range"]::-moz-range-thumb {
				height: 20px;
				width: 20px;
				background-color: #070452;
				border-radius: 50%;
				border: none;
				transition: 0.2s ease-in-out;
			}

			/* Hover, active & focus Thumb: Webkit */
			input[type="range"]::-webkit-slider-thumb:hover {
				box-shadow: 0 0 0 10px rgba(255, 85, 0, 0.1);
			}
			input[type="range"]:active::-webkit-slider-thumb {
				box-shadow: 0 0 0 13px rgba(255, 85, 0, 0.2);
			}
			input[type="range"]:focus::-webkit-slider-thumb {
				box-shadow: 0 0 0 13px rgba(255, 85, 0, 0.2);
			}

			/* Hover, active & focus Thumb: Firefox */
			input[type="range"]::-moz-range-thumb:hover {
				box-shadow: 0 0 0 10px rgba(255, 85, 0, 0.1);
			}
			input[type="range"]:active::-moz-range-thumb {
				box-shadow: 0 0 0 13px rgba(255, 85, 0, 0.2);
			}
			input[type="range"]:focus::-moz-range-thumb {
				box-shadow: 0 0 0 13px rgba(255, 85, 0, 0.2);
			}
		}
	}
}
</style>
